import {
  lendAPIConfig,
  LendAPIProduct,
  LendAPIProductHooks,
  GATrackingConfig,
  KameleoonTrackingConfig,
  PrequalCompleteForcedLoginConfig,
  ResumeLinkForcedLoginConfig,
  getAllProducts,
} from '../lendAPIConfig.types';
import { handleGATracking } from './lendAPIGAHelper';
import { handleKameleoonTracking } from './lendAPIKameleoonHelper';
import { isValidProductType } from './lendAPIRouteValidation';

export function getProductConfigById(productId: string | undefined): LendAPIProduct | null {
  if (!productId) return null;
  const numericProductId = parseInt(productId, 10);
  const product = getAllProducts().find((p) => p.productId === numericProductId);

  if (!product) {
    console.warn(`[lendAPIConfig] No product found for productId: ${productId}`);
  }

  return product || null;
}

export function resolveProductHooks(
  productType: string,
  product: LendAPIProduct | null
): LendAPIProductHooks | undefined {
  if (!isValidProductType(productType)) {
    console.warn(`[lendAPIConfig] Invalid productType: ${productType}`);
    return undefined;
  }

  const productHooks = product?.hooks || {};
  const productTypeConfig = lendAPIConfig.productTypes[productType];
  const productTypeHooks = productTypeConfig?.hooks || {};

  if (!product?.hooks && !productTypeConfig?.hooks) {
    console.warn(
      `[lendAPIConfig] No hooks configured for product ID ${
        product?.productId || 'unknown'
      } or product type ${productType}`
    );
    return undefined;
  }

  if (!product && productTypeHooks) {
    console.log(
      `[lendAPIConfig] Product not found, using productType '${productType}' default hooks`
    );
  }

  return {
    prequalContactInfo: productHooks.prequalContactInfo || productTypeHooks.prequalContactInfo,
    prequalComplete: productHooks.prequalComplete || productTypeHooks.prequalComplete,
    fullAppContactInfo: productHooks.fullAppContactInfo || productTypeHooks.fullAppContactInfo,
    fullAppComplete: productHooks.fullAppComplete || productTypeHooks.fullAppComplete,
    enrichLendAPIApplication:
      productHooks.enrichLendAPIApplication || productTypeHooks.enrichLendAPIApplication,
    processCollateralURL:
      productHooks.processCollateralURL || productTypeHooks.processCollateralURL,
  };
}

export function resolveGATrackingConfig(
  productType: string,
  product: LendAPIProduct | null
): GATrackingConfig | undefined {
  if (!isValidProductType(productType)) {
    console.warn(`[lendAPIConfig] Invalid productType: ${productType}`);
    return undefined;
  }

  if (product?.gaTracking && Object.keys(product.gaTracking).length > 0) {
    console.log(
      `[lendAPIConfig][GA][TRACKING] Using product-specific GA configuration for product ID ${product.productId}`
    );
    return product.gaTracking;
  }

  const productTypeConfig = lendAPIConfig.productTypes[productType];
  const gaTracking = productTypeConfig?.gaTracking;

  if (!product && gaTracking) {
    console.log(
      `[lendAPIConfig] Product not found, using productType '${productType}' default GA tracking config`
    );
  }

  return gaTracking;
}

export function resolveKameleoonTrackingConfig(
  productType: string,
  product: LendAPIProduct | null
): KameleoonTrackingConfig | undefined {
  if (!isValidProductType(productType)) {
    console.warn(`[lendAPIConfig] Invalid productType: ${productType}`);
    return undefined;
  }

  if (product?.kameleoonTracking && Object.keys(product.kameleoonTracking).length > 0) {
    console.log(
      `[lendAPIConfig][Kameleoon][TRACKING] Using product-specific Kameleoon configuration for product ID ${product.productId}`
    );
    return product.kameleoonTracking;
  }

  const productTypeConfig = lendAPIConfig.productTypes[productType];
  const kameleoonTracking = productTypeConfig?.kameleoonTracking;

  if (!product && kameleoonTracking) {
    console.log(
      `[lendAPIConfig] Product not found, using productType '${productType}' default Kameleoon tracking config`
    );
  }

  return kameleoonTracking;
}

export function processPerformanceTracking(
  tabName: string,
  productConfig: LendAPIProduct | null,
  productType: string,
  payload?: any
): void {
  if (!tabName) return;

  const gaConfig = resolveGATrackingConfig(productType, productConfig);
  if (gaConfig?.enabled) {
    handleGATracking(tabName, gaConfig, productType, payload);
  }

  const kameleoonConfig = resolveKameleoonTrackingConfig(productType, productConfig);
  if (kameleoonConfig?.enabled) {
    handleKameleoonTracking(tabName, productType, kameleoonConfig.customGoalMapping);
  }
}

export function resolvePrequalCompleteForcedLoginConfig(
  productType: string,
  product: LendAPIProduct | null
): PrequalCompleteForcedLoginConfig | undefined {
  if (!isValidProductType(productType)) {
    return undefined;
  }
  if (
    product?.prequalCompleteForcedLogin &&
    Object.keys(product.prequalCompleteForcedLogin).length > 0
  ) {
    return product.prequalCompleteForcedLogin;
  }
  return lendAPIConfig.productTypes[productType]?.prequalCompleteForcedLogin;
}

export function resolveResumeLinkForcedLoginConfig(
  productType: string,
  product: LendAPIProduct | null
): ResumeLinkForcedLoginConfig | undefined {
  if (!isValidProductType(productType)) {
    return undefined;
  }
  if (product?.resumeLinkForcedLogin && Object.keys(product.resumeLinkForcedLogin).length > 0) {
    return product.resumeLinkForcedLogin;
  }
  return lendAPIConfig.productTypes[productType]?.resumeLinkForcedLogin;
}
